import React, { useState } from "react";
import { KeyRound } from "lucide-react";
import { changePassword } from "../../api/auth";

const inputClass = "mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#1234CD] focus:ring-2 focus:ring-[#1234CD]/10";

export default function ChangePasswordPanel({ className = "" }) {
  const [values, setValues] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const update = (key) => (event) => setValues((current) => ({ ...current, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");
    if (values.newPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (values.newPassword !== values.confirmPassword) {
      setError("New password and confirmation do not match.");
      return;
    }
    if (values.currentPassword === values.newPassword) {
      setError("New password must be different from your current password.");
      return;
    }

    try {
      setSaving(true);
      const result = await changePassword(values.currentPassword, values.newPassword);
      setValues({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setMessage(result?.message || "Password changed successfully.");
    } catch (requestError) {
      setError(requestError.message || "Unable to change your password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className={`rounded-2xl border border-slate-100 bg-white p-5 shadow-sm ${className}`}>
      <div className="mb-1 flex items-center gap-2">
        <KeyRound size={19} className="text-[#1234CD]" />
        <h3 className="font-semibold text-[#071C50]">Change password</h3>
      </div>
      <p className="mb-5 text-sm text-slate-500">Use at least 8 characters. Existing refresh sessions will be revoked for your security.</p>
      {(error || message) && (
        <div role="status" className={`mb-4 rounded-xl border px-4 py-3 text-sm ${error ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
          {error || message}
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-3">
        <label className="block text-sm font-medium text-slate-700">Current password<input className={inputClass} type="password" autoComplete="current-password" value={values.currentPassword} onChange={update("currentPassword")} maxLength={72} /></label>
        <label className="block text-sm font-medium text-slate-700">New password<input className={inputClass} type="password" autoComplete="new-password" value={values.newPassword} onChange={update("newPassword")} maxLength={72} /></label>
        <label className="block text-sm font-medium text-slate-700">Confirm new password<input className={inputClass} type="password" autoComplete="new-password" value={values.confirmPassword} onChange={update("confirmPassword")} maxLength={72} /></label>
      </div>
      <div className="mt-5 flex justify-end">
        <button type="submit" disabled={saving || !values.currentPassword || !values.newPassword || !values.confirmPassword} className="rounded-xl bg-[#1234CD] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0E2AA7] disabled:opacity-50">
          {saving ? "Changing password..." : "Change password"}
        </button>
      </div>
    </form>
  );
}
